import { useEffect, useMemo, useState } from "react";

// Hooks
import { useValidations } from "@/Common/hooks/Validations";

// Components
import InputValue from "@Layouts/components/InputComponents/InputValue";
import TierAlertError from "@Layouts/components/Tiers/TierAlertError";

// MUI
import { FormControl, FormControlLabel, Grid, Radio, RadioGroup, Stack, Typography } from "@mui/material";

// Types
import { NumberOrString } from "@/Layouts/components/InputComponents/InputValue/types";
import CustomizedSwitches from "@/Layouts/components/InputComponents/CustomizedSwitches";

// Constants
const DEFAULT_WEIGHTING = {
	weightingType: "UNITS" as const,
	isSurveyParticipation: true,
	customWeights: [] as { make: string; weight: number }[],
};

const WEIGHTING_TYPES = {
	UNITS: "UNITS",
	CUSTOM: "CUSTOM",
} as const;

const INPUT_CONFIG = {
	type: "Percent" as const,
	min: 0,
	max: 100,
	decimalsEditable: true,
	decimals: 2,
	width: "100px",
};

const VALIDATION_RULES = {
	CUSTOM_WEIGHTS_SUM: 100,
	ERROR_MESSAGE: "Custom weights must add up to 100%.",
};

const getValidationConfig = (innerWeighting: IWeighting) => {
	return [
		{
			definition: {
				useKey: "weighting",
				operators: {
					conditions: [
						{
							type: "COLUMN",
							on: "weight",
							operation: "SUM",
							compare: "===",
							limit: VALIDATION_RULES.CUSTOM_WEIGHTS_SUM,
						},
					],
				},
				errorsMap: ["weight"],
				message: VALIDATION_RULES.ERROR_MESSAGE,
				validationType: "OPERATION",
			},
			section: innerWeighting?.customWeights || [],
		},
	];
};

const createCustomWeights = (makes: string[]) => makes?.map((make) => ({ make, weight: 0 }));

const getInitialWeighting = (
	weighting: IWeighting,
	makes: string[],
	createCustomWeights: (makes: string[]) => { make: string; weight: number }[]
) => {
	if (!weighting?.customWeights?.length && makes?.length) {
		return {
			...weighting,
			isSurveyParticipation: true,
			customWeights: createCustomWeights(makes),
		};
	}
	return weighting;
};

const checkIsWeightingValid = (innerWeighting: IWeighting, isValid: boolean) => {
	const isCustomType = innerWeighting.weightingType === WEIGHTING_TYPES.CUSTOM;
	const valid = isValid || !isCustomType;
	return valid;
};

const radioStyle = () => ({
	"& .MuiSvgIcon-root": {
		fontSize: 20,
	},
});

interface IWeighting {
	weightingType: string;
	isSurveyParticipation: boolean;
	customWeights: { make: string; weight: number }[];
}

interface IWeightingProps {
	makes?: string[];
	weighting?: IWeighting;
	weightLabel?: string;
	readOnly?: boolean;
	onActionUpdate?: (weighting: IWeighting) => void;
	onActionValidation?: (isValid: boolean) => void;
}

const Weighting = (props: IWeightingProps) => {
	const {
		makes = [],
		weighting = DEFAULT_WEIGHTING,
		weightLabel = "units sold",
		readOnly = false,
		onActionUpdate = () => {},
		onActionValidation = () => {},
	} = props;

	const [innerWeighting, setInnerWeighting] = useState<IWeighting>(getInitialWeighting(weighting, makes, createCustomWeights));
	const { isValid, getErrorsMessages, validate } = useValidations();
	const errorsMessages = getErrorsMessages();

	const handleIsSurveyParticipationChange = () => {
		const updated = { ...innerWeighting, isSurveyParticipation: !innerWeighting.isSurveyParticipation };
		setInnerWeighting(updated);
	};

	const handleWeightingOptionChange = (_e: React.ChangeEvent<HTMLInputElement>, value: string) => {
		const updated = {
			...innerWeighting,
			weightingType: value as string,
			customWeights: value === WEIGHTING_TYPES.CUSTOM ? createCustomWeights(makes) : [],
		};
		setInnerWeighting(updated);
	};

	const handleCustomWeightChange = (index: number, value: NumberOrString) => {
		const updated = { ...innerWeighting };
		updated.customWeights[index].weight = value ? Number(value) : 0;
		setInnerWeighting(updated);
	};

	const isCustomWeighting = useMemo(() => innerWeighting?.weightingType === WEIGHTING_TYPES.CUSTOM, [innerWeighting?.weightingType]);

	const CustomWeightsSection = useMemo(
		() => (
			<Stack>
				<Stack direction="row" mt={2} spacing={1}>
					<Stack spacing={1} mr={2}>
						{innerWeighting.customWeights?.map((element: { make: string; weight: number }) => (
							<Stack key={`label_${element.make}`} justifyContent="center" sx={{ height: "30px" }}>
								<Typography className="draw-label" sx={{ margin: 0 }}>
									{element.make}
								</Typography>
							</Stack>
						))}
					</Stack>
					<Stack spacing={1}>
						{innerWeighting.customWeights?.map((element: { make: string; weight: number }, index: number) => (
							<InputValue
								key={`input_${element.make}`}
								data={[INPUT_CONFIG]}
								value={element.weight || 0}
								error={!isValid}
								readOnly={readOnly}
								sx={{ width: INPUT_CONFIG.width }}
								onActionUpdate={(value) => handleCustomWeightChange(index, value)}
							/>
						))}
					</Stack>
				</Stack>
				<Grid container rowSpacing={0.2} alignItems="flex-start" mt={1}>
					{errorsMessages.map((message, key) => (
						<TierAlertError key={`error_weighting_${message}_${key}`} text={message} />
					))}
				</Grid>
			</Stack>
		),
		[innerWeighting.customWeights, isValid, readOnly, errorsMessages]
	);

	useMemo(() => {
		if (innerWeighting?.weightingType === WEIGHTING_TYPES.CUSTOM) {
			validate(getValidationConfig(innerWeighting));
		}
	}, [innerWeighting, validate]);

	useEffect(() => {
		const valid = checkIsWeightingValid(innerWeighting, isValid);
		onActionValidation(valid);
		if (!valid) {
			return;
		}
		onActionUpdate(innerWeighting);
	}, [innerWeighting]);

	return (
		<Stack mt={2} display={"flex"} flexDirection="column" gap={2}>
			<CustomizedSwitches
				disabled={readOnly}
				checked={innerWeighting.isSurveyParticipation}
				textChecked={"Requires survey participation"}
				textUnChecked={"Requires survey participation"}
				onChange={handleIsSurveyParticipationChange}
			/>
			<FormControl>
				<RadioGroup
					aria-labelledby="weight-buttons-group"
					value={innerWeighting?.weightingType || WEIGHTING_TYPES.UNITS}
					name="weight-buttons-group"
					className="radio-buttons-small regular-blue-color"
					onChange={handleWeightingOptionChange}
				>
					<FormControlLabel
						value={WEIGHTING_TYPES.UNITS}
						control={<Radio disabled={readOnly} sx={radioStyle} />}
						label={`Weight performance by ${weightLabel}`}
					/>
					<FormControlLabel
						value={WEIGHTING_TYPES.CUSTOM}
						control={<Radio disabled={readOnly} sx={radioStyle} />}
						label="Use custom weights"
					/>
				</RadioGroup>
			</FormControl>
			{isCustomWeighting && CustomWeightsSection}
		</Stack>
	);
};

export default Weighting;
